import { useState, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { auditService } from '@/services/platform/audit';
import { maskAddressLine, maskEmail, maskName, maskPhone } from '@/lib/pii';
import { cn } from '@/lib/utils';
import type { AuditEntity } from '@/types/platform';

type Kind = 'email' | 'phone' | 'name' | 'address';

const MASK: Record<Kind, (v: string | null | undefined) => string> = {
  email: maskEmail,
  phone: maskPhone,
  name: maskName,
  address: maskAddressLine,
};

interface Props {
  value?: string | null;
  kind: Kind;
  /** Audit entity tag for the reveal log. Required when reveal should be logged. */
  entity?: AuditEntity;
  /** Audit entity id. Required when reveal should be logged. */
  entityId?: string;
  /** Field name that goes into the audit record (e.g. 'contactEmail'). Defaults to `kind`. */
  fieldName?: string;
  /** Render variant — table cells stay tight, detail panes can breathe. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Renders sensitive PII values masked by default. Click the eye to reveal —
 * the reveal posts an audit log entry to the runtime (compliance addendum
 * §A.5). Optimistic on the FE; reveal happens immediately even if the audit
 * write fails (logged to console for ops follow-up).
 */
export function PIIField({
  value,
  kind,
  entity,
  entityId,
  fieldName,
  size = 'sm',
  className,
}: Props) {
  const [revealed, setRevealed] = useState(false);

  const onToggle = useCallback(() => {
    if (!value) return;
    if (!revealed && entity && entityId) {
      // Fire-and-forget: do not block the reveal if audit write fails.
      auditService
        .recordPiiAccess({
          entity,
          entityId,
          fields: [fieldName ?? kind],
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[pii-audit] failed to record reveal', err);
        });
    }
    setRevealed((r) => !r);
  }, [revealed, value, entity, entityId, fieldName, kind]);

  if (!value) {
    return <span className="text-muted-foreground">—</span>;
  }

  const display = revealed ? value : MASK[kind](value);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        size === 'sm' ? 'text-[13px]' : 'text-sm',
        className,
      )}
    >
      <span className={cn(revealed ? 'text-foreground' : 'text-muted-foreground', kind === 'email' || kind === 'phone' ? 'font-mono text-[12px]' : '')}>
        {display}
      </span>
      <button
        type="button"
        onClick={onToggle}
        className="rounded p-0.5 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={revealed ? `Hide ${kind}` : `Reveal ${kind}`}
        title={revealed ? `Hide ${kind}` : `Reveal ${kind} (logged)`}
      >
        {revealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
      </button>
    </span>
  );
}
